<template>
  <div class="d-flex flex-column flex-grow-1 rounded p-3">
    <BaseToolBar
      title="Indicateurs véhicules"
      subtitle="Statistiques et données sur le parc de véhicules"
    >
      <div class="btn-group mr-1" role="group">
        <BaseButton
          class="btn btn-primary ml-3"
          v-tooltip="'Exporter les statistiques'"
          @click="exportStats"
          :loading="loading"
          text="Exporter (.xls)"
        ></BaseButton>
      </div>
      <router-link to="/vehicules" class="btn btn-secondary">
        Retour à la liste
      </router-link>
    </BaseToolBar>

    <!-- Loading indicator -->
    <div v-if="loading" class="d-flex justify-content-center my-5">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Chargement...</span>
      </div>
    </div>

    <div v-else class="row mt-3">
      <!-- Cartes de statistiques générales -->
      <div class="col-md-3 mb-4">
        <div class="card h-100 border-primary">
          <div class="card-body text-center">
            <h5 class="card-title text-primary">Total des véhicules</h5>
            <div class="display-4">{{ vehicules.length }}</div>
          </div>
        </div>
      </div>
      <div class="col-md-3 mb-4">
        <div class="card h-100 border-info">
          <div class="card-body text-center">
            <h5 class="card-title text-info">Nouveaux véhicules</h5>
            <div class="display-4">{{ vehiculeNew.length }}</div>
          </div>
        </div>
      </div>
      <div class="col-md-3 mb-4">
        <div class="card h-100 border-success">
          <div class="card-body text-center">
            <h5 class="card-title text-success">Affectés</h5>
            <div class="display-4">{{ vehiculesAffectes.length }}</div>
          </div>
        </div>
      </div>
      <div class="col-md-3 mb-4">
        <div class="card h-100 border-warning">
          <div class="card-body text-center">
            <h5 class="card-title text-warning">Non affectés</h5>
            <div class="display-4">{{ vehiculesNonAffectes.length }}</div>
          </div>
        </div>
      </div>

      <!-- Répartition par société -->
      <div class="col-md-6 mb-4">
        <div class="card h-100">
          <div class="card-header bg-primary text-white">
            <h5 class="mb-0">Répartition des véhicules par société</h5>
          </div>
          <div class="card-body">
            <table class="table table-striped">
              <thead>
                <tr>
                  <th>Société</th>
                  <th class="text-center">Nombre de véhicules</th>
                  <th class="text-center">Pourcentage</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(count, societe) in vehiculesParSociete"
                  :key="societe"
                >
                  <td>{{ societe || "Non défini" }}</td>
                  <td class="text-center">{{ count }}</td>
                  <td class="text-center">
                    {{ ((count / vehicules.length) * 100).toFixed(1) }}%
                    <div class="progress mt-1" style="height: 5px;">
                      <div
                        class="progress-bar bg-primary"
                        :style="`width: ${(count / vehicules.length) * 100}%`"
                      ></div>
                    </div>
                  </td>
                </tr>
                <tr v-if="Object.keys(vehiculesParSociete).length === 0">
                  <td colspan="3" class="text-center">
                    Aucune donnée disponible
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Répartition par catégorie -->
      <div class="col-md-6 mb-4">
        <div class="card h-100">
          <div class="card-header bg-success text-white">
            <h5 class="mb-0">Répartition par catégorie de véhicule</h5>
          </div>
          <div class="card-body">
            <table class="table table-striped">
              <thead>
                <tr>
                  <th>Catégorie</th>
                  <th class="text-center">Nombre</th>
                  <th class="text-center">Pourcentage</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(count, categorie) in vehiculesParCategorie"
                  :key="categorie"
                >
                  <td>{{ categorie }}</td>
                  <td class="text-center">{{ count }}</td>
                  <td class="text-center">
                    {{ ((count / vehicules.length) * 100).toFixed(1) }}%
                    <div class="progress mt-1" style="height: 5px;">
                      <div
                        class="progress-bar bg-success"
                        :style="`width: ${(count / vehicules.length) * 100}%`"
                      ></div>
                    </div>
                  </td>
                </tr>
                <tr v-if="Object.keys(vehiculesParCategorie).length === 0">
                  <td colspan="3" class="text-center">
                    Aucune donnée disponible
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Top 5 des véhicules les plus affectés -->
      <div class="col-md-6 mb-4">
        <div class="card h-100">
          <div class="card-header bg-info text-white">
            <h5 class="mb-0">
              Top 5 des véhicules avec le plus d'affectations
            </h5>
          </div>
          <div class="card-body">
            <table class="table table-striped">
              <thead>
                <tr>
                  <th>Véhicule</th>
                  <th>Immatriculation</th>
                  <th class="text-center">Nb affectations</th>
                  <th class="text-center">Détails</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="vehicule in topVehiculesAffectes" :key="vehicule.id">
                  <td>{{ vehicule.modele || "Non défini" }}</td>
                  <td>{{ vehicule.immatriculation || "Non défini" }}</td>
                  <td class="text-center">
                    <span class="badge badge-info">
                      {{
                        vehicule.collaborateurs
                          ? vehicule.collaborateurs.length
                          : 0
                      }}
                    </span>
                  </td>
                  <td class="text-center">
                    <router-link
                      :to="'/vehicules/detail/' + vehicule.id"
                      class="btn btn-sm btn-outline-info"
                    >
                      Détails
                    </router-link>
                  </td>
                </tr>
                <tr v-if="topVehiculesAffectes.length === 0">
                  <td colspan="4" class="text-center">
                    Aucune donnée disponible
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Véhicules arrivant à échéance -->
      <div class="col-md-6 mb-4">
        <div class="card h-100">
          <div class="card-header bg-warning text-white">
            <h5 class="mb-0">
              Véhicules arrivant à échéance (30 prochains jours)
            </h5>
          </div>
          <div class="card-body">
            <table class="table table-striped">
              <thead>
                <tr>
                  <th>Véhicule</th>
                  <th>Immatriculation</th>
                  <th class="text-center">Date de restitution</th>
                  <th class="text-center">Jours restants</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="vehicule in vehiculesProchesRestitution"
                  :key="vehicule.id"
                >
                  <td>{{ vehicule.modele || "Non défini" }}</td>
                  <td>{{ vehicule.immatriculation || "Non défini" }}</td>
                  <td class="text-center">
                    {{ formatDate(vehicule.date_restitution) }}
                  </td>
                  <td class="text-center">
                    <span
                      class="badge"
                      :class="getJoursRestantsClass(vehicule.joursRestants)"
                    >
                      {{ vehicule.joursRestants }} jours
                    </span>
                  </td>
                </tr>
                <tr v-if="vehiculesProchesRestitution.length === 0">
                  <td colspan="4" class="text-center">
                    Aucun véhicule n'arrive à échéance dans les 30 prochains
                    jours
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Statistiques financières -->
      <div class="col-md-12 mb-4">
        <div class="card h-100">
          <div class="card-header bg-dark text-white">
            <h5 class="mb-0">Statistiques financières</h5>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-4">
                <div class="card mb-3">
                  <div class="card-body">
                    <h6 class="card-title text-muted">Coût mensuel total</h6>
                    <h3>{{ formatCurrency(coutMensuelTotal) }}</h3>
                  </div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="card mb-3">
                  <div class="card-body">
                    <h6 class="card-title text-muted">
                      Coût moyen par véhicule
                    </h6>
                    <h3>{{ formatCurrency(coutMoyenParVehicule) }}</h3>
                  </div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="card mb-3">
                  <div class="card-body">
                    <h6 class="card-title text-muted">Valeur totale du parc</h6>
                    <h3>{{ formatCurrency(valeurTotaleParc) }}</h3>
                  </div>
                </div>
              </div>
            </div>

            <!-- Tableau des coûts par société -->
            <table class="table table-striped mt-3">
              <thead>
                <tr>
                  <th>Société</th>
                  <th class="text-center">Nombre de véhicules</th>
                  <th class="text-center">Coût mensuel total</th>
                  <th class="text-center">Coût moyen par véhicule</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(stats, societe) in coutParSociete" :key="societe">
                  <td>{{ societe || "Non défini" }}</td>
                  <td class="text-center">{{ stats.count }}</td>
                  <td class="text-center">{{ formatCurrency(stats.total) }}</td>
                  <td class="text-center">
                    {{ formatCurrency(stats.total / stats.count) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { errHandler } from "@/helpers.js";
import BaseToolBar from "@/components/bases/ToolBar.vue";
import BaseButton from "@/components/bases/Button.vue";
import moment from "moment";

export default {
  components: {
    BaseToolBar,
    BaseButton
  },
  data() {
    return {
      data: {},
      vehicules: [],
      loading: false
    };
  },
  methods: {
    exportStats() {
      this.loading = true;
      this.$http
        .get("/vehicules-stats-export")
        .then(response => {
          const anchor = document.createElement("a");
          anchor.href =
            "data:text/csv;charset=utf-8," + encodeURIComponent(response.data);
          anchor.target = "_blank";
          anchor.download = "statistiques-vehicules.csv";
          anchor.click();
        })
        .catch(error => console.error(errHandler(error)))
        .finally(() => (this.loading = false));
    },
    getCategorieDescription(categorieId) {
      if (!this.data || !this.data.categories) return "Non défini";
      const categorie = this.data.categories.find(
        cat => cat.id === categorieId
      );
      return categorie ? categorie.description : "Non défini";
    },
    formatDate(date) {
      if (!date) return "Non défini";
      return moment(date).format("DD/MM/YYYY");
    },
    formatCurrency(value) {
      if (!value && value !== 0) return "N/A";
      return new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR"
      }).format(value);
    },
    getJoursRestantsClass(jours) {
      if (jours <= 7) return "badge-danger";
      if (jours <= 15) return "badge-warning";
      return "badge-info";
    }
  },
  mounted() {
    this.loading = true;
    Promise.all([
      this.$http.get("/vehicules"),
      this.$http.get("/vehicules-data")
    ])
      .then(([vehiculesResp, dataResp]) => {
        this.vehicules = vehiculesResp.data;
        this.data = dataResp.data;
      })
      .catch(error => console.error(errHandler(error)))
      .finally(() => (this.loading = false));
  },
  computed: {
    vehiculeNew() {
      return this.vehicules.filter(vehicule => vehicule.new === true);
    },
    vehiculesAffectes() {
      return this.vehicules.filter(
        vehicule =>
          vehicule.collaborateurs && vehicule.collaborateurs.length > 0
      );
    },
    vehiculesNonAffectes() {
      return this.vehicules.filter(
        vehicule =>
          !vehicule.collaborateurs || vehicule.collaborateurs.length === 0
      );
    },
    vehiculesParSociete() {
      const societeCount = {};

      this.vehicules.forEach(vehicule => {
        if (!vehicule.collaborateurs || vehicule.collaborateurs.length === 0) {
          // Non affecté
          const societe = "Non affecté";
          societeCount[societe] = (societeCount[societe] || 0) + 1;
          return;
        }

        // Prendre la dernière affectation pour déterminer la société
        const collaborateurs = vehicule.collaborateurs || [];
        if (collaborateurs.length > 0) {
          // Trouver la dernière affectation par date
          const derniereAffectation = collaborateurs.reduce(
            (latest, current) => {
              if (!latest.date_affectation) return current;
              if (!current.date_affectation) return latest;

              return moment(current.date_affectation).isAfter(
                moment(latest.date_affectation)
              )
                ? current
                : latest;
            },
            {}
          );

          if (
            derniereAffectation.collaborateur &&
            derniereAffectation.collaborateur.societe
          ) {
            const societe = derniereAffectation.collaborateur.societe;
            societeCount[societe] = (societeCount[societe] || 0) + 1;
          } else {
            const societe = "Société non définie";
            societeCount[societe] = (societeCount[societe] || 0) + 1;
          }
        } else {
          const societe = "Non affecté";
          societeCount[societe] = (societeCount[societe] || 0) + 1;
        }
      });

      return societeCount;
    },
    vehiculesParCategorie() {
      const categorieCount = {};

      this.vehicules.forEach(vehicule => {
        const categorie = this.getCategorieDescription(vehicule.categorie_id);
        categorieCount[categorie] = (categorieCount[categorie] || 0) + 1;
      });

      return categorieCount;
    },
    topVehiculesAffectes() {
      // Trie les véhicules par nombre d'affectations décroissant et prend les 5 premiers
      return [...this.vehicules]
        .filter(v => v.collaborateurs && v.collaborateurs.length > 0)
        .sort(
          (a, b) =>
            (b.collaborateurs?.length || 0) - (a.collaborateurs?.length || 0)
        )
        .slice(0, 5);
    },
    vehiculesProchesRestitution() {
      const now = moment();
      const dans30Jours = moment().add(30, "days");

      return this.vehicules
        .filter(vehicule => {
          if (!vehicule.date_restitution) return false;
          const dateRestitution = moment(vehicule.date_restitution);
          return (
            dateRestitution.isAfter(now) &&
            dateRestitution.isBefore(dans30Jours)
          );
        })
        .map(vehicule => {
          const joursRestants = moment(vehicule.date_restitution).diff(
            now,
            "days"
          );
          return { ...vehicule, joursRestants };
        })
        .sort((a, b) => a.joursRestants - b.joursRestants);
    },
    coutMensuelTotal() {
      return this.vehicules.reduce((total, vehicule) => {
        return total + (parseFloat(vehicule.mensualite) || 0);
      }, 0);
    },
    coutMoyenParVehicule() {
      if (this.vehicules.length === 0) return 0;
      return this.coutMensuelTotal / this.vehicules.length;
    },
    valeurTotaleParc() {
      return this.vehicules.reduce((total, vehicule) => {
        return total + (parseFloat(vehicule.valeur_vehicule) || 0);
      }, 0);
    },
    coutParSociete() {
      const societeStats = {};

      this.vehicules.forEach(vehicule => {
        let societe = "Non affecté";

        if (vehicule.collaborateurs && vehicule.collaborateurs.length > 0) {
          const derniereAffectation = vehicule.collaborateurs.reduce(
            (latest, current) => {
              if (!latest.date_affectation) return current;
              if (!current.date_affectation) return latest;

              return moment(current.date_affectation).isAfter(
                moment(latest.date_affectation)
              )
                ? current
                : latest;
            },
            {}
          );

          if (
            derniereAffectation.collaborateur &&
            derniereAffectation.collaborateur.societe
          ) {
            societe = derniereAffectation.collaborateur.societe;
          } else {
            societe = "Société non définie";
          }
        }

        if (!societeStats[societe]) {
          societeStats[societe] = { count: 0, total: 0 };
        }

        societeStats[societe].count++;
        societeStats[societe].total += parseFloat(vehicule.mensualite) || 0;
      });

      return societeStats;
    }
  }
};
</script>

<style lang="css">
.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  transition: all 0.3s ease;
}

.card:hover {
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.card-header {
  font-weight: 500;
}

.table th {
  background-color: #f8f9fa;
}

.progress {
  border-radius: 0.5rem;
  overflow: hidden;
}

.badge {
  padding: 0.35em 0.65em;
  font-size: 0.75em;
}

.badge-danger {
  background-color: #dc3545;
  color: white;
}

.badge-warning {
  background-color: #ffc107;
  color: #212529;
}

.badge-info {
  background-color: #17a2b8;
  color: white;
}

.display-4 {
  font-weight: 300;
  line-height: 1.2;
}

.border-primary {
  border-top: 4px solid #007bff !important;
}

.border-info {
  border-top: 4px solid #17a2b8 !important;
}

.border-success {
  border-top: 4px solid #28a745 !important;
}

.border-warning {
  border-top: 4px solid #ffc107 !important;
}
</style>
